import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'
import { useEffect, useState, type FormEvent } from 'react'
import { ApiError } from '../../api/client'
import { discoveryTabTarget } from './discoveryDoors'
import {
  discoverySetupQuery,
  rangeFormQuery,
  removeRange,
  removeRule,
  ruleFormQuery,
  saveCredential,
  saveRange,
  saveRule,
  saveSchedule,
  saveTelemetryProfile,
  scanRulesQuery,
  scheduleFormQuery,
  searchTelemetryCi,
  telemetryProfileQuery,
  telemetryQuery,
  vaultChecksQuery,
  vaultFormQuery,
  vaultQuery,
} from '../../api/settingsSoftsee'
import { hrefOf } from '../../overview/href'
import { DiscoveryAssistant } from './DiscoveryAssistant'
import { Modal } from '../../ui/Modal'
import { newAlert } from '../../ui/notify'
import { SelectPicker } from '../../ui/SelectPicker'
import { Spinner } from '../../ui/Spinner'
/* Capul de ecran al setarilor noi (`h4` + `label.color`, uneltele plutite
   INAINTEA titlului). Piesa e comuna zonei de setari si sta in
   `ui/SettingsParts`, care isi aduce si foaia. */
import { SettingsHead } from '../../ui/SettingsParts'

const STATE_DONE = 1
const STATE_BLOCKING = 2
const STATE_OPTIONAL = 3

const MARKS: Record<number, [string, string, string]> = {
  [STATE_DONE]: ['label-success', 'fa-check', 'Done'],
  [STATE_BLOCKING]: ['label-warning', 'fa-exclamation', 'Needs you'],
  [STATE_OPTIONAL]: ['label-default', 'fa-minus', 'Optional'],
}

const COLLECTOR_EXPLAIN: Record<number, string> = {
  1: 'The applications and Windows services installed on the machine.',
  2: 'Which network ports the machine is listening on, and which program owns each one.',
  3: 'Which other machines this one talks to, and on which ports. This is what builds the dependency map.',
  4: 'Scans the configured IP ranges for machines that have no agent. Normally only switched on for one machine per site.',
}

const DEFAULT_INTERVAL: Record<number, number> = { 1: 3600, 2: 3600, 3: 300, 4: 86400 }

function num(row: Record<string, unknown>, key: string): number {
  return Number(row[key] ?? 0)
}

function str(row: Record<string, unknown>, key: string): string {
  return String(row[key] ?? '')
}

/**
 * `options.read_via` al unui credential VMware. Lipsa lui: un credential vechi
 * cu adresa era citit de platforma (asa ramane), unul fara adresa - de releu.
 */
/**
 * Campurile in plus ale unui tip de credential. VMware (4): cine citeste,
 * adresa, portul, certificatul. Check Point (8): portul si certificatul - consola
 * de management o citeste numai releul, deci fara "Read by" si fara adresa.
 */
export function credentialFields(type: number): { port: boolean; tls: boolean; readVia: boolean; host: boolean } {
  const vmware = type === 4
  const tls = vmware || type === 8
  return { port: tls, tls, readVia: vmware, host: vmware }
}

export function vmwareReadVia(raw: unknown, host = ''): string {
  let options: unknown = raw
  if (typeof raw === 'string') {
    try {
      options = JSON.parse(raw || '{}')
    } catch {
      options = {}
    }
  }
  const via = options && typeof options === 'object' ? (options as Record<string, unknown>).read_via : undefined
  if (via === 'server' || via === 'relay') return via
  return host.trim() !== '' ? 'server' : 'relay'
}

function fields(form: HTMLFormElement): Record<string, string | number> {
  const out: Record<string, string | number> = {}
  const data = new FormData(form)
  data.forEach((value, key) => {
    if (typeof value === 'string') out[key] = value
  })
  return out
}

function formPayload(form: HTMLFormElement): FormData {
  return new FormData(form)
}

export function DiscoverySetupPanel({ openAssistant = false }: { openAssistant?: boolean } = {}) {
  const navigate = useNavigate()
  const page = useQuery(discoverySetupQuery())
  const [advanced, setAdvanced] = useState(false)
  /* "Set up discovery" (`DiscoveryAssistant`): din buton, sau deschis direct
     din iconita din bara (`?assistant=1`). */
  const [assistant, setAssistant] = useState(openAssistant)
  useEffect(() => {
    if (openAssistant) setAssistant(true)
  }, [openAssistant])
  if (page.error instanceof ApiError && page.error.status === 403) {
    return <div className="alert alert-danger">You do not have access to this action.</div>
  }
  if (!page.data) return <Spinner />
  const steps = page.data.steps
  const blocking = steps.filter((step) => num(step, 'state') === STATE_BLOCKING).length
  /* Din Policies ramai in Policies - vezi `discoveryDoors.ts`. */
  const openTab = (href: string) => {
    const tab = href.replace(/^settings\/softsee\//, '')
    const to = discoveryTabTarget(tab)
    if (to.startsWith('/settings/softsee/')) void navigate({ to: '/settings/softsee/$tab', params: { tab } })
    else void navigate({ to })
  }
  return (
    <div id="content">
      {/* Capul zonei, ca pe celelalte ecrane de setari. Aici n-are unelte: tot
          ce se apasa sta in randurile de dedesubt. */}
      <SettingsHead strong="Discovery" rest="setup" subtitle="what has to be set, in the order it has to be set" />
      <p>
        <button type="button" className="btn btn-primary btn-sm" id="setupAssistantOpen" onClick={() => setAssistant(true)}>
          <i className="fa fa-magic"></i> Set up discovery
        </button>{' '}
        <span className="text-muted small">A proposal for the current company: relay, ranges and schedule, office by office.</span>
      </p>
      <DiscoveryAssistant open={assistant} onClose={() => setAssistant(false)} />
      <p className="text-muted">
        {blocking === 0 ? (
          'Everything that has to be configured is. The steps below say what is running.'
        ) : (
          <>
            <strong>{blocking}</strong> of the four steps still need something. Until they do,
            discovery finds less than it could — each one says what.
          </>
        )}
      </p>
      <table className="table table-hover">
        <tbody>
          {steps.map((step) => {
            const state = num(step, 'state')
            const [labelClass, icon, labelText] = MARKS[state] ?? MARKS[STATE_OPTIONAL]
            const elsewhere = str(step, 'elsewhere')
            const next = str(step, 'next')
            return (
              <tr key={num(step, 'number')}>
                <td style={{ width: 40 }} className="text-center text-muted">
                  {num(step, 'number')}
                </td>
                <td style={{ width: 110 }}>
                  <span className={'label ' + labelClass}>
                    <i className={'fa ' + icon}></i> {labelText}
                  </span>
                </td>
                <td>
                  <strong>{str(step, 'title')}</strong>
                  <br />
                  <span className="text-muted">{str(step, 'summary')}</span>
                  {next !== '' && state !== STATE_DONE ? (
                    <>
                      <br />
                      <span className="text-muted small">
                        <i className="fa fa-arrow-right"></i> {next}
                      </span>
                    </>
                  ) : null}
                </td>
                <td className="text-right text-nowrap" style={{ width: 190 }}>
                  {elsewhere !== '' ? (
                    /* `elsewhere` vine de la API ca adresa de portal
                       (`inventoree/servers`, `settings/inventoree`), iar
                       amandoua sunt ecrane v4. Fara `hrefOf` butonul iesea
                       din aplicatie ca sa fie intors inapoi cu 302. */
                    <a className="btn btn-xs btn-primary" href={hrefOf(elsewhere) ?? '/' + elsewhere}>
                      Go there
                    </a>
                  ) : (
                    <button
                      className="btn btn-xs btn-default setupOpen"
                      data-url={'/app/' + str(step, 'url')}
                      type="button"
                      onClick={() => openTab(str(step, 'url'))}
                    >
                      Open
                    </button>
                  )}
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>
      <p>
        <a
          href="#"
          id="setupAdvanced"
          onClick={(event) => {
            event.preventDefault()
            setAdvanced((v) => !v)
          }}
        >
          <i className={'fa ' + (advanced ? 'fa-caret-down' : 'fa-caret-right')}></i> Fine tuning
        </a>
      </p>
      <div id="setupAdvancedBody" style={{ display: advanced ? 'block' : 'none' }}>
        <p className="text-muted">
          None of this has to be touched for discovery to work. It is here for when the defaults
          are wrong for a particular customer.
        </p>
        <table className="table table-hover">
          <tbody>
            <tr>
              <td>
                <strong>Ignore rules</strong>
                <br />
                <span className="text-muted">
                  Things found by a scan that should not be recorded — a printer&apos;s spooler, a
                  monitoring port, an address that belongs to somebody else.
                </span>
              </td>
              <td className="text-right" style={{ width: 120 }}>
                <button
                  className="btn btn-xs btn-default setupOpen"
                  type="button"
                  onClick={() => openTab('settings/softsee/scan_rules')}
                >
                  Open
                </button>
              </td>
            </tr>
            <tr>
              <td>
                <strong>Scan schedule</strong>
                <br />
                <span className="text-muted">
                  When each relay scans. Outside working hours is usually the right answer.
                </span>
              </td>
              <td className="text-right">
                <button
                  className="btn btn-xs btn-default setupOpen"
                  type="button"
                  onClick={() => openTab('settings/softsee/scan_rules')}
                >
                  Open
                </button>
              </td>
            </tr>
            <tr>
              <td>
                <strong>Change thresholds</strong>
                <br />
                <span className="text-muted">
                  How many times a connection has to be seen before a new dependency is announced.
                  Without them, every first sighting becomes an alert.
                </span>
                <br />
                <span className="text-muted small">Settings → Inventoree → Relation rules</span>
              </td>
              <td className="text-right">
                <a className="btn btn-xs btn-primary" href="/app/settings/inventoree">
                  Go there
                </a>
              </td>
            </tr>
            <tr>
              <td>
                <strong>Which relations are allowed</strong>
                <br />
                <span className="text-muted">
                  The vocabulary of the CMDB. Already filled in, and rarely changed.
                </span>
                <br />
                <span className="text-muted small">Settings → Inventoree → Relation rules</span>
              </td>
              <td className="text-right">
                <a className="btn btn-xs btn-primary" href="/app/settings/inventoree">
                  Go there
                </a>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  )
}

export function TelemetryPanel() {
  const query = useQueryClient()
  const page = useQuery(telemetryQuery())
  const [formId, setFormId] = useState<number | null>(null)
  if (page.error instanceof ApiError && page.error.status === 403) {
    return <div className="alert alert-danger">You do not have access to this action.</div>
  }
  if (!page.data) return <Spinner />
  const scopesByProfile: Record<number, Array<{ type: string; label: string }>> = {}
  for (const assignment of page.data.assignments) {
    const profileId = num(assignment, 'profile_id')
    const scope = num(assignment, 'scope_type')
    const labels = page.data.scopeLabels
    ;(scopesByProfile[profileId] ??= []).push({
      type: labels[String(scope)] ?? '?',
      label:
        scope === 1
          ? 'Everything'
          : str(assignment, 'company_name') ||
            str(assignment, 'class_name') ||
            str(assignment, 'ci_name') ||
            '#' + num(assignment, 'scope_id'),
    })
  }
  return (
    <>
      <div id="content">
        <SettingsHead
          strong="Data"
          rest="collection"
          subtitle="what each agent gathers, and from which machines"
          tools={
            page.data.canCreate ? (
              <button className="btn btn-success btn-sm" id="profileAdd" type="button" onClick={() => setFormId(0)}>
                <i className="fas fa-plus-circle"></i> Add profile
              </button>
            ) : undefined
          }
        />
        <div className="telemetryNotifications"></div>
        <p className="text-muted">
          The agent installed on each machine can gather four kinds of information. A <em>profile</em> is
          a saved combination of which of those four are switched on, how often each one runs, and
          which machines it applies to. <strong>Nothing is gathered until you switch something on.</strong>
        </p>
        <table className="table table-striped table-hover">
          <thead>
            <tr>
              <th>Profile</th>
              <th>Description</th>
              <th className="text-center">Switched on</th>
              <th>Applies to</th>
              <th className="text-center">Status</th>
              <th className="text-right">&nbsp;</th>
            </tr>
          </thead>
          <tbody>
            {page.data.profiles.length === 0 ? (
              <tr>
                <td colSpan={6}>
                  <span className="label label-default">No profiles yet.</span> Add one to start
                  gathering anything.
                </td>
              </tr>
            ) : (
              page.data.profiles.map((profile) => {
                const scopes = scopesByProfile[num(profile, 'ID')] ?? []
                return (
                  <tr key={num(profile, 'ID')}>
                    <td>
                      {str(profile, 'name')}
                      {num(profile, 'is_default') ? <span className="label label-info">default</span> : null}
                    </td>
                    <td>{str(profile, 'description')}</td>
                    <td className="text-center">
                      {num(profile, 'enabled_collectors') === 0 ? (
                        <span className="label label-warning">nothing yet</span>
                      ) : (
                        <span className="label label-success">{num(profile, 'enabled_collectors')} of 4</span>
                      )}
                    </td>
                    <td>
                      {scopes.length === 0 ? (
                        <span className="label label-default">no machines</span>
                      ) : (
                        scopes.map((scope, i) => (
                          <span key={i} className="label label-default" title={scope.type}>
                            {scope.label}
                          </span>
                        ))
                      )}
                    </td>
                    <td className="text-center">
                      {num(profile, 'status') ? (
                        <span className="label label-success">active</span>
                      ) : (
                        <span className="label label-default">disabled</span>
                      )}
                    </td>
                    <td className="text-right text-nowrap">
                      <button
                        className="btn btn-xs btn-primary profileEdit"
                        data-id={num(profile, 'ID')}
                        type="button"
                        onClick={() => setFormId(num(profile, 'ID'))}
                      >
                        <i className="fas fa-sliders-h"></i> {page.data.canWrite ? 'Edit' : 'View'}
                      </button>
                    </td>
                  </tr>
                )
              })
            )}
          </tbody>
        </table>
        <p className="text-muted small">
          Where two profiles cover the same machine, the narrowest rule wins — one machine beats
          its class, which beats a customer, which beats everything.
        </p>
      </div>
      {/* Aceeasi fereastra ca la Create Company: valul din spate, `body.modal-open`
          si Escape le aduce `Modal`. Titlul si butoanele stau in fereastra, iar
          butonul de salvare isi gaseste formularul dupa `form=`. */}
      <Modal
        id="telemetryModal"
        open={formId !== null}
        large
        onClose={() => setFormId(null)}
        title={formId === 0 ? 'Add profile' : 'Edit profile'}
        footer={
          <>
            <button type="button" className="btn btn-default" onClick={() => setFormId(null)}>
              {page.data.canWrite ? 'Cancel' : 'Close'}
            </button>
            {page.data.canWrite ? (
              <button type="submit" form="telemetryForm" className="btn btn-primary">
                {formId === 0 ? 'Add profile' : 'Save profile'}
              </button>
            ) : null}
          </>
        }
      >
        {formId !== null ? (
          <TelemetryProfileForm
            id={formId}
            onSaved={async () => {
              setFormId(null)
              await query.invalidateQueries({ queryKey: ['settings', 'softsee', 'telemetry'] })
            }}
          />
        ) : null}
      </Modal>
    </>
  )
}

/* Inchiderea si salvarea stau in subsolul ferestrei, deci formularul nu mai
   primeste `onClose`. */
function TelemetryProfileForm({ id, onSaved }: { id: number; onSaved(): Promise<void> }) {
  const page = useQuery(telemetryProfileQuery(id))
  const [scopeRows, setScopeRows] = useState<Array<{ type: number; id: number; priority: number; label: string }>>([])
  const [seeded, setSeeded] = useState(false)
  /* Sertarul consolei e controlat: starea profilului sta aici. */
  const [status, setStatus] = useState('1')
  useEffect(() => {
    if (!page.data || seeded) return
    const existing = page.data.assignments.map((assignment) => ({
      type: num(assignment, 'scope_type'),
      id: num(assignment, 'scope_id'),
      priority: num(assignment, 'priority') || 100,
      label: str(assignment, 'company_name') || str(assignment, 'class_name') || str(assignment, 'ci_name'),
    }))
    setScopeRows(existing.length ? existing : [{ type: 1, id: 0, priority: 100, label: '' }])
    setStatus(page.data.profile === null || num(page.data.profile, 'status') ? '1' : '0')
    setSeeded(true)
  }, [page.data, seeded])
  if (!page.data) return <div>Loading…</div>
  const editing = page.data.profile !== null
  const profile = page.data.profile ?? {}
  const onSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    try {
      const result = await saveTelemetryProfile(id, formPayload(event.currentTarget))
      if (result.status && result.status !== 'OK') {
        newAlert(result.message ?? 'That could not be saved.', 'danger', 7000, document.querySelector('.telemetryNotifications'))
        return
      }
      await onSaved()
    } catch (err) {
      newAlert(err instanceof Error ? err.message : 'That could not be saved.', 'danger', 7000, document.querySelector('.telemetryNotifications'))
    }
  }
  return (
    <form id="telemetryForm" data-action={'saveProfileForm/' + (editing ? num(profile, 'ID') : 0)} onSubmit={onSubmit}>
      <div className="row">
        <div className="col-md-6 form-group">
          <label htmlFor="profileName">Name</label>
          <input type="text" className="form-control" id="profileName" name="name" maxLength={100} defaultValue={str(profile, 'name')} placeholder="Servers, production" required />
        </div>
        <div className={'col-md-' + (editing ? '4' : '6') + ' form-group'}>
          <label htmlFor="profileDescription">Description</label>
          <input type="text" className="form-control" id="profileDescription" name="description" maxLength={255} defaultValue={str(profile, 'description')} placeholder="Optional" />
        </div>
        {editing ? (
          <div className="col-md-2 form-group">
            <label htmlFor="profileStatus">Status</label>
            {num(profile, 'is_default') ? <input type="hidden" name="status" value="1" /> : null}
            <SelectPicker
              id="profileStatus"
              className="form-control"
              name={num(profile, 'is_default') ? undefined : 'status'}
              title="Select.."
              value={status}
              onChange={setStatus}
              disabled={num(profile, 'is_default') > 0}
              options={[
                { value: '1', label: 'Active' },
                { value: '0', label: 'Disabled' },
              ]}
            />
          </div>
        ) : null}
      </div>
      <hr />
      <h5>
        <strong>What it gathers</strong>
      </h5>
      <CollectorsTable collectors={page.data.collectors} labels={page.data.collectorLabels} />
      <hr />
      <h5>
        <strong>Which machines</strong>
      </h5>
      <p className="text-muted small">
        The narrowest rule wins: a rule for one machine beats a rule for its class, which beats a
        rule for a customer, which beats the rule for everything.
        <br />A profile with no machines gathers nothing.
      </p>
      <div className="row scopeHeadings">
        <div className="col-md-4">
          <label>Applies to</label>
        </div>
        <div className="col-md-5">
          <label>Which one</label>
        </div>
        <div className="col-md-2">
          <label>Priority</label>
        </div>
        <div className="col-md-1">&nbsp;</div>
      </div>
      <div id="scopeRows">
        {scopeRows.map((row, index) => (
          <ScopeRow
            key={index}
            row={row}
            index={index}
            labels={page.data.scopeLabels}
            companies={page.data.companies}
            classes={page.data.classes}
            kinds={page.data.kinds}
            onChange={(next) => setScopeRows((rows) => rows.map((r, i) => (i === index ? next : r)))}
            onRemove={() => setScopeRows((rows) => rows.filter((_, i) => i !== index))}
          />
        ))}
      </div>
      <p className="text-muted small">
        Priority only breaks a tie between two profiles applied to the same thing. A narrower
        scope always wins, whatever the numbers say. Leave it at 100 unless you have a reason.
      </p>
      {page.data.canWrite ? (
        <button
          type="button"
          className="btn btn-success btn-sm"
          id="scopeAdd"
          onClick={() => setScopeRows((rows) => [...rows, { type: 1, id: 0, priority: 100, label: '' }])}
        >
          <i className="fas fa-plus-circle"></i> Add machines
        </button>
      ) : null}
    </form>
  )
}

function CollectorsTable({
  collectors,
  labels,
}: {
  collectors: Record<string, Record<string, unknown>>
  labels: Record<string, string>
}) {
  return (
    <table className="table table-condensed">
      <thead>
        <tr>
          <th style={{ width: 50 }} className="text-center">
            On
          </th>
          <th>What it gathers</th>
          <th style={{ width: 150 }}>How often</th>
          <th style={{ width: 130 }}>Most rows</th>
        </tr>
      </thead>
      <tbody>
        {[1, 2, 3, 4].map((collector) => {
          const row = collectors[String(collector)] ?? collectors[collector] ?? {}
          return (
            <tr key={collector} className="collectorRow">
              <td className="text-center" style={{ verticalAlign: 'middle' }}>
                <input
                  type="checkbox"
                  className="collectorEnabled"
                  name={'enabled_' + collector}
                  value="1"
                  data-default={DEFAULT_INTERVAL[collector]}
                  defaultChecked={num(row, 'enabled') > 0}
                />
              </td>
              <td>
                <strong>{labels[String(collector)] ?? labels[collector]}</strong>
                <br />
                <span className="text-muted small">{COLLECTOR_EXPLAIN[collector]}</span>
              </td>
              <td>
                <div className="input-group">
                  <input
                    className="form-control input-sm collectorInterval"
                    type="number"
                    min={0}
                    step={60}
                    name={'interval_' + collector}
                    defaultValue={num(row, 'interval_seconds')}
                  />
                  <span className="input-group-addon">sec</span>
                </div>
              </td>
              <td>
                <input
                  className="form-control input-sm collectorRows"
                  type="number"
                  min={0}
                  max={20000}
                  step={100}
                  name={'max_rows_' + collector}
                  defaultValue={num(row, 'max_rows_per_report')}
                />
              </td>
            </tr>
          )
        })}
      </tbody>
    </table>
  )
}

function ScopeRow({
  row,
  index,
  labels,
  companies,
  classes,
  kinds,
  onChange,
  onRemove,
}: {
  row: { type: number; id: number; priority: number; label: string }
  /* Randul se repeta, iar sertarul consolei isi leaga bucatile de `id`, deci
     fiecare rand isi poarta numarul ca id-urile sa nu se repete in pagina. */
  index: number
  labels: Record<string, string>
  companies: Array<Record<string, unknown>>
  classes: Array<Record<string, unknown>>
  kinds: Record<string, string>
  onChange(next: { type: number; id: number; priority: number; label: string }): void
  onRemove(): void
}) {
  const [term, setTerm] = useState(row.label)
  const [hits, setHits] = useState<Array<{ id: number; name: string; hint?: string }>>([])
  return (
    <div className="row scopeRow" style={{ marginBottom: 8 }}>
      <div className="col-md-4">
        <SelectPicker
          id={'scopeType' + index}
          className="form-control input-sm scopeType"
          name="scope_type[]"
          title="Select.."
          value={String(row.type)}
          onChange={(value) => onChange({ ...row, type: Number(value), id: 0, label: '' })}
          options={Object.entries(labels).map(([value, label]) => ({ value, label }))}
        />
      </div>
      <div className="col-md-5 scopeTarget scopeTarget1" style={{ display: row.type === 1 ? undefined : 'none' }}>
        <p className="form-control-static text-muted small">Every machine of every customer.</p>
      </div>
      <div className="col-md-5 scopeTarget scopeTarget2" style={{ display: row.type === 2 ? undefined : 'none' }}>
        {/* `disabled` ramane: asa randul care nu se potriveste nu pleaca in POST. */}
        <SelectPicker
          id={'scopeCompany' + index}
          className="form-control input-sm"
          name="scope_company[]"
          title="Pick a customer"
          disabled={row.type !== 2}
          value={row.type === 2 ? String(row.id || '') : ''}
          onChange={(value) => onChange({ ...row, id: Number(value) })}
          options={companies.map((company) => ({ value: String(num(company, 'ID')), label: str(company, 'name') }))}
        />
      </div>
      <div className="col-md-5 scopeTarget scopeTarget3" style={{ display: row.type === 3 ? undefined : 'none' }}>
        <SelectPicker
          id={'scopeClass' + index}
          className="form-control input-sm"
          name="scope_class[]"
          title="Pick a class"
          disabled={row.type !== 3}
          value={row.type === 3 ? String(row.id || '') : ''}
          onChange={(value) => onChange({ ...row, id: Number(value) })}
          options={classes.map((cls) => ({
            value: String(num(cls, 'ID')),
            label: str(cls, 'name') + ' — ' + (kinds[String(num(cls, 'kind'))] ?? ''),
          }))}
        />
      </div>
      <div className="col-md-5 scopeTarget scopeTarget4" style={{ display: row.type === 4 ? undefined : 'none' }}>
        <input
          type="text"
          className="form-control input-sm scopeCiSearch"
          autoComplete="off"
          placeholder="Type at least two letters"
          disabled={row.type !== 4}
          value={term}
          onChange={async (event) => {
            const value = event.target.value
            setTerm(value)
            onChange({ ...row, id: 0, label: value })
            if (value.length < 2) {
              setHits([])
              return
            }
            setHits(await searchTelemetryCi(value))
          }}
        />
        <input type="hidden" className="scopeCiId" name="scope_ci[]" value={row.type === 4 ? row.id || '' : ''} disabled={row.type !== 4} />
        <div className="list-group scopeCiResults" style={{ marginTop: 4, display: hits.length ? 'block' : 'none' }}>
          {hits.map((ci) => (
            <a
              key={ci.id}
              href="#"
              className="list-group-item"
              onClick={(event) => {
                event.preventDefault()
                setTerm(ci.name)
                setHits([])
                onChange({ ...row, id: ci.id, label: ci.name })
              }}
            >
              {ci.name}
              {ci.hint ? ' — ' + ci.hint : ''}
            </a>
          ))}
        </div>
      </div>
      <div className="col-md-2">
        <input
          type="number"
          className="form-control input-sm"
          name="scope_priority[]"
          value={row.priority}
          min={1}
          max={9999}
          aria-label="Priority"
          onChange={(event) => onChange({ ...row, priority: Number(event.target.value) })}
        />
      </div>
      <div className="col-md-1 text-right">
        <button type="button" className="btn btn-xs btn-danger scopeRemove" title="Remove" onClick={onRemove}>
          <i className="fas fa-trash"></i>
        </button>
      </div>
    </div>
  )
}

export function CredentialsPanel() {
  const query = useQueryClient()
  const page = useQuery(vaultQuery())
  const [formId, setFormId] = useState<number | null>(null)
  const [checksId, setChecksId] = useState<number | null>(null)
  if (page.error instanceof ApiError && page.error.status === 403) {
    return <div className="alert alert-danger">You do not have access to this action.</div>
  }
  if (!page.data) return <Spinner />
  return (
    <>
      <div id="content">
        <SettingsHead
          strong="Scanning"
          rest="credentials"
          subtitle="accounts the platform uses to log in to what it scans"
          tools={
            <button className="btn btn-success btn-sm" id="credentialAdd" type="button" onClick={() => setFormId(0)}>
              <i className="fas fa-plus-circle"></i> Add a credential
            </button>
          }
        />
        <div className="credentialsNotifications"></div>
        <p className="text-muted">
          Each one says <em>which addresses</em> it applies to and <em>in what order</em> it is tried.
          Secrets are encrypted before they are stored and are never shown on this list — reading one
          back is a separate action, and is recorded every time.
        </p>
        <div className="row">
          <div className="col-md-12">
            <table className="table table-striped table-hover">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Type</th>
                  <th>User</th>
                  <th>Applies to</th>
                  <th className="text-center">Order</th>
                  <th className="text-center">Health</th>
                  <th className="text-center">Status</th>
                  <th className="text-right">&nbsp;</th>
                </tr>
              </thead>
              <tbody>
                {page.data.items.length === 0 ? (
                  <tr>
                    <td colSpan={8}>
                      <span className="label label-default">No credentials yet.</span>
                    </td>
                  </tr>
                ) : (
                  page.data.items.map((item) => {
                    const expired = str(item, 'expires_at') !== '' && Date.parse(str(item, 'expires_at')) < Date.now()
                    return (
                      <tr key={num(item, 'ID')}>
                        <td>
                          {str(item, 'name')}
                          {str(item, 'company_name') ? <div className="text-muted small">{str(item, 'company_name')}</div> : null}
                        </td>
                        <td>{page.data.typeLabels[String(num(item, 'type'))] ?? ''}</td>
                        <td>{str(item, 'username')}</td>
                        <td>
                          {num(item, 'scope_type') === 1 ? (
                            <>
                              {str(item, 'ip_from')} – {str(item, 'ip_to')}
                            </>
                          ) : (
                            <>
                              {page.data.scopeLabels[String(num(item, 'scope_type'))] ?? ''} #{num(item, 'scope_id')}
                            </>
                          )}
                        </td>
                        <td className="text-center">{num(item, 'try_order')}</td>
                        <td className="text-center">
                          {num(item, 'failures_24h') > 0 ? (
                            <span className="label label-danger">{num(item, 'failures_24h')} failed today</span>
                          ) : str(item, 'last_ok') !== '' ? (
                            <span className="label label-success">working</span>
                          ) : (
                            <span className="label label-default">never tested</span>
                          )}
                          {str(item, 'last_ok') !== '' ? (
                            <div className="text-muted small">last ok {str(item, 'last_ok').slice(0, 16)}</div>
                          ) : null}
                        </td>
                        <td className="text-center">
                          {expired ? (
                            <span className="label label-danger">expired</span>
                          ) : num(item, 'status') === 1 ? (
                            <span className="label label-success">active</span>
                          ) : (
                            <span className="label label-default">off</span>
                          )}
                        </td>
                        <td className="text-right">
                          <button
                            className="btn btn-default btn-xs credentialChecks"
                            data-id={num(item, 'ID')}
                            title="Recent connection results"
                            type="button"
                            onClick={() => setChecksId(num(item, 'ID'))}
                          >
                            <i className="fa fa-history"></i>
                          </button>
                          {page.data.canReveal ? (
                            <button className="btn btn-default btn-xs credentialReveal" data-id={num(item, 'ID')} type="button">
                              <i className="fa fa-eye"></i>
                            </button>
                          ) : null}
                          {page.data.canEdit ? (
                            <button
                              className="btn btn-default btn-xs credentialEdit"
                              data-id={num(item, 'ID')}
                              type="button"
                              onClick={() => setFormId(num(item, 'ID'))}
                            >
                              <i className="fa fa-pencil-alt"></i>
                            </button>
                          ) : null}
                        </td>
                      </tr>
                    )
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
      {/* Aceeasi fereastra ca la Create Company: valul din spate, `body.modal-open`
          si Escape le aduce `Modal`. Titlul si butoanele stau in fereastra, iar
          butonul de salvare isi gaseste formularul dupa `form=`. */}
      <Modal
        id="credentialModal"
        open={formId !== null || checksId !== null}
        large
        onClose={() => {
          setFormId(null)
          setChecksId(null)
        }}
        title={checksId !== null ? 'Recent connection results' : formId === 0 ? 'Add a credential' : 'Edit credential'}
        footer={
          checksId !== null ? (
            <button type="button" className="btn btn-default" onClick={() => setChecksId(null)}>
              Close
            </button>
          ) : (
            <>
              <button type="button" className="btn btn-default" onClick={() => setFormId(null)}>
                Cancel
              </button>
              <button type="submit" form="credentialForm" className="btn btn-primary">
                Save
              </button>
            </>
          )
        }
      >
        {formId !== null ? (
          <CredentialForm
            id={formId}
            onSaved={async () => {
              setFormId(null)
              await query.invalidateQueries({ queryKey: ['settings', 'softsee', 'vault'] })
            }}
          />
        ) : null}
        {checksId !== null ? <CredentialChecks id={checksId} /> : null}
      </Modal>
    </>
  )
}

/* Inchiderea si salvarea stau in subsolul ferestrei, deci formularul nu mai
   primeste `onClose`. */
function CredentialForm({ id, onSaved }: { id: number; onSaved(): Promise<void> }) {
  const page = useQuery(vaultFormQuery(id))
  const [type, setType] = useState(1)
  const [scope, setScope] = useState(1)
  /* Sertarele consolei sunt controlate: valoarea lor sta aici si se seamana
     cand raspunde serverul. */
  const [company, setCompany] = useState('0')
  const [status, setStatus] = useState('1')
  /* VMware: cine citeste - releul de pe reteaua clientului sau platforma. */
  const [readVia, setReadVia] = useState('relay')
  useEffect(() => {
    if (!page.data) return
    const row = page.data.credential
    setCompany(String(num(row ?? {}, 'company_id') || 0))
    setStatus(row === null || num(row, 'status') === 1 ? '1' : '0')
    if (!row) return
    setType(num(row, 'type') || 1)
    setScope(num(row, 'scope_type') || 1)
    setReadVia(vmwareReadVia(row.options, str(row, 'host')))
  }, [page.data])
  if (!page.data) return <div>Loading…</div>
  const cred = page.data.credential ?? {}
  const isEdit = page.data.credential !== null
  const options = (() => {
    try {
      const raw = cred.options
      return raw && typeof raw === 'object' ? (raw as Record<string, unknown>) : JSON.parse(String(raw || '{}'))
    } catch {
      return {}
    }
  })()
  const onSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    try {
      const result = await saveCredential(fields(event.currentTarget))
      if (result.status && result.status !== 'OK') {
        newAlert(result.message ?? 'That could not be saved.', 'danger', 7000, document.querySelector('.credentialsNotifications'))
        return
      }
      await onSaved()
    } catch (err) {
      newAlert(err instanceof Error ? err.message : 'That could not be saved.', 'danger', 7000, document.querySelector('.credentialsNotifications'))
    }
  }
  return (
    <form id="credentialForm" onSubmit={onSubmit}>
      <input type="hidden" name="ID" value={isEdit ? num(cred, 'ID') : 0} />
      <div className="row">
        <div className="col-md-6 form-group">
          <label htmlFor="credName">Name</label>
          <input type="text" className="form-control" id="credName" name="name" maxLength={190} defaultValue={str(cred, 'name')} placeholder="e.g. Windows service account" />
        </div>
        <div className="col-md-6 form-group">
          <label htmlFor="credCompany">Customer</label>
          <SelectPicker
            id="credCompany"
            className="form-control"
            name="company_id"
            title="All customers"
            value={company}
            onChange={setCompany}
            options={[
              { value: '0', label: 'All customers' },
              ...page.data.companies.map((row) => ({ value: String(num(row, 'ID')), label: str(row, 'name') })),
            ]}
          />
        </div>
      </div>
      <div className="row">
        <div className="col-md-4 form-group">
          <label htmlFor="credType">Type</label>
          <SelectPicker
            id="credType"
            className="form-control"
            name="type"
            title="Select.."
            value={String(type)}
            onChange={(value) => setType(Number(value))}
            options={Object.entries(page.data.typeLabels).map(([value, label]) => ({ value, label }))}
          />
        </div>
        <div className="col-md-4 form-group">
          <label htmlFor="credUsername">User name</label>
          <input type="text" className="form-control" id="credUsername" name="username" maxLength={190} defaultValue={str(cred, 'username')} />
        </div>
        <div className="col-md-4 form-group">
          <label htmlFor="credSecret">
            Secret {isEdit ? <span className="text-muted small">leave empty to keep the current one</span> : null}
          </label>
          <input type="password" className="form-control" id="credSecret" name="secret" autoComplete="new-password" />
        </div>
      </div>
      <div className="row" id="credSnmpRow" style={{ display: type === 3 ? undefined : 'none' }}>
        <div className="col-md-4 form-group">
          <label htmlFor="credCommunity">SNMP community</label>
          <input type="password" className="form-control" id="credCommunity" name="snmp_community" autoComplete="new-password" />
        </div>
      </div>
      <div className="row" id="credHostRow" style={{ display: credentialFields(type).port ? undefined : 'none' }}>
        <div className="col-md-4 form-group" style={{ display: credentialFields(type).readVia ? undefined : 'none' }}>
          <label htmlFor="credReadVia">Read by</label>
          <SelectPicker
            id="credReadVia"
            className="form-control"
            name="read_via"
            title="Select.."
            value={readVia}
            onChange={setReadVia}
            options={[
              { value: 'relay', label: 'The relay on the customer network' },
              { value: 'server', label: 'The platform (vCenter reachable from the internet)' },
            ]}
          />
        </div>
        <div className="col-md-5 form-group" style={{ display: credentialFields(type).host ? undefined : 'none' }}>
          <label htmlFor="credHost">
            Address {readVia === 'relay' ? <span className="text-muted small">optional - the relay uses the addresses below</span> : null}
          </label>
          <input type="text" className="form-control" id="credHost" name="host" maxLength={190} placeholder="vcenter.example.local or the ESXi address" defaultValue={str(cred, 'host')} />
        </div>
        <div className="col-md-3 form-group">
          <label htmlFor="credPort">Port</label>
          <input type="number" className="form-control" id="credPort" name="port" min={1} max={65535} placeholder="443" defaultValue={num(cred, 'port') || ''} />
        </div>
      </div>
      <div className="row" id="credTlsRow" style={{ display: credentialFields(type).tls ? undefined : 'none' }}>
        <div className="col-md-5 form-group">
          <div className="checkbox">
            <label htmlFor="credSelfSigned">
              <input type="checkbox" id="credSelfSigned" name="accept_self_signed" value="1" defaultChecked={options.accept_self_signed === true} /> Accept a
              self-signed certificate
            </label>
          </div>
          <span className="text-muted small">
            The first certificate seen on each address is remembered; a different one later is refused. Use a read-only account.
          </span>
        </div>
        <div className="col-md-7 form-group">
          <label htmlFor="credFingerprint">
            Certificate fingerprint <span className="text-muted small">SHA-256, optional - pins it</span>
          </label>
          <input
            type="text"
            className="form-control"
            id="credFingerprint"
            name="tls_fingerprint"
            maxLength={110}
            placeholder="AB:CD:…"
            defaultValue={String(options.tls_fingerprint ?? '')}
          />
        </div>
      </div>
      <div className="row" id="credAwsRow" style={{ display: type === 5 ? undefined : 'none' }}>
        <div className="col-md-12 form-group">
          <label htmlFor="credRegions">Regions</label>
          <input
            type="text"
            className="form-control"
            id="credRegions"
            name="regions"
            placeholder="eu-central-1, eu-west-1"
            defaultValue={Array.isArray(options.regions) ? options.regions.join(', ') : ''}
          />
        </div>
      </div>
      <div className="row" id="credAzureRow" style={{ display: type === 6 ? undefined : 'none' }}>
        <div className="col-md-6 form-group">
          <label htmlFor="credTenant">Directory (tenant) ID</label>
          <input type="text" className="form-control" id="credTenant" name="tenant_id" maxLength={190} defaultValue={String(options.tenant_id ?? '')} />
        </div>
        <div className="col-md-6 form-group">
          <label htmlFor="credSubscription">Subscription ID</label>
          <input type="text" className="form-control" id="credSubscription" name="subscription_id" maxLength={190} defaultValue={String(options.subscription_id ?? '')} />
        </div>
      </div>
      <hr />
      <div className="row">
        <div className="col-md-4 form-group">
          <label htmlFor="credScopeType">Applies to</label>
          <SelectPicker
            id="credScopeType"
            className="form-control"
            name="scope_type"
            title="Select.."
            value={String(scope)}
            onChange={(value) => setScope(Number(value))}
            options={Object.entries(page.data.scopeLabels).map(([value, label]) => ({ value, label }))}
          />
        </div>
        <div className="col-md-4 form-group credScopeRange" style={{ display: scope === 1 ? undefined : 'none' }}>
          <label htmlFor="credIpFrom">From address</label>
          <input type="text" className="form-control" id="credIpFrom" name="ip_from" defaultValue={str(cred, 'ip_from')} placeholder="10.0.0.1" />
        </div>
        <div className="col-md-4 form-group credScopeRange" style={{ display: scope === 1 ? undefined : 'none' }}>
          <label htmlFor="credIpTo">To address</label>
          <input type="text" className="form-control" id="credIpTo" name="ip_to" defaultValue={str(cred, 'ip_to')} placeholder="10.0.0.254" />
        </div>
        <div className="col-md-4 form-group credScopeId" style={{ display: scope !== 1 ? undefined : 'none' }}>
          <label htmlFor="credScopeId">Applies to item</label>
          <input type="number" className="form-control" id="credScopeId" name="scope_id" defaultValue={num(cred, 'scope_id') || ''} />
        </div>
      </div>
      <div className="row">
        <div className="col-md-4 form-group">
          <label htmlFor="credTryOrder">
            Try order <span className="text-muted small">lower is tried first</span>
          </label>
          <input type="number" className="form-control" id="credTryOrder" name="try_order" defaultValue={isEdit ? num(cred, 'try_order') : 100} />
        </div>
        <div className="col-md-4 form-group">
          <label htmlFor="credExpires">
            Expires <span className="text-danger credExpiryRequired">*</span>
          </label>
          <input type="date" className="form-control" id="credExpires" name="expires_at" defaultValue={str(cred, 'expires_at').slice(0, 10)} />
          <span className="text-muted small credExpiryRequired">An account that logs in has to be rotated, so it needs a date.</span>
        </div>
        <div className="col-md-4 form-group">
          <label htmlFor="credStatus">Status</label>
          <SelectPicker
            id="credStatus"
            className="form-control"
            name="status"
            title="Select.."
            value={status}
            onChange={setStatus}
            options={[
              { value: '1', label: 'Active' },
              { value: '0', label: 'Switched off' },
            ]}
          />
        </div>
      </div>
    </form>
  )
}

/* Titlul si butonul de inchidere stau in fereastra, ca la formular. */
function CredentialChecks({ id }: { id: number }) {
  const page = useQuery(vaultChecksQuery(id))
  if (!page.data) return <div>Loading…</div>
  if (page.data.checks.length === 0) return <span className="label label-default">No checks yet.</span>
  return (
    <table className="table table-condensed">
      <tbody>
        {page.data.checks.map((check, i) => (
          <tr key={i}>
            <td>{page.data.results[String(num(check, 'result'))] ?? ''}</td>
            <td>{str(check, 'target_ip')}</td>
            <td>{str(check, 'message')}</td>
          </tr>
        ))}
      </tbody>
    </table>
  )
}

export function ScanRulesPanel() {
  const query = useQueryClient()
  const page = useQuery(scanRulesQuery())
  const [rangeId, setRangeId] = useState<number | null>(null)
  const [ruleId, setRuleId] = useState<number | null>(null)
  const [scheduleRelay, setScheduleRelay] = useState<number | null>(null)
  if (page.error instanceof ApiError && page.error.status === 403) {
    return <div className="alert alert-danger">You do not have access to this action.</div>
  }
  if (!page.data) return <Spinner />
  const hasRelays = page.data.relays.length > 0
  const close = () => {
    setRangeId(null)
    setRuleId(null)
    setScheduleRelay(null)
  }
  const refresh = async () => {
    close()
    await query.invalidateQueries({ queryKey: ['settings', 'softsee', 'scan-rules'] })
  }
  return (
    <>
      <div id="content">
        {/*
         * Capul zonei, ca pe Customers si pe cele zece din Inventoree: titlul
         * `h4` de 22px si uneltele plutite la dreapta INAINTEA lui.
         *
         * Inainte era `h3` (26px, alt etaj de titlu decat tot restul setarilor),
         * iar butonul statea DUPA subtitlu - masurat in Edge cu foile de
         * productie, plutirea il cobora la y=81, adica la 16,7px deasupra
         * tabelului, in loc de pe linia titlului.
         */}
        <SettingsHead
          strong="Scan"
          rest="scope"
          subtitle="the addresses each relay scans, and what it skips"
          tools={
            page.data.canWrite && hasRelays ? (
              <button className="btn btn-success btn-sm" id="rangeAdd" type="button" onClick={() => setRangeId(0)}>
                <i className="fas fa-plus-circle"></i> Add a range
              </button>
            ) : undefined
          }
        />
        <div className="scanRulesNotifications"></div>
        {!hasRelays ? (
          <div className="alert alert-warning">
            <i className="fas fa-info-circle"></i> <strong>No relay yet.</strong> A range is scanned
            by one machine on the customer&apos;s network, and none is marked as a relay.
            <br />
            <br />
            Mark one in <a href="/app/inventoree/servers">Inventoree → the machine → Settings</a>, with{' '}
            <em>Set as AD Relay</em>. It should be a machine that stays on and can reach the subnets
            you want scanned. Come back here afterwards and it will be in the list.
          </div>
        ) : (
          <>
            <p className="text-muted">A relay picks changes up at its next settings fetch, not immediately.</p>
            <div className="row">
              <div className="col-md-12">
                <table className="table table-striped table-hover">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Relay</th>
                      <th>Customer</th>
                      <th>Office</th>
                      <th>Addresses</th>
                      <th>Runs</th>
                      <th className="text-center">Mode</th>
                      <th className="text-center">Active</th>
                      <th className="text-right">&nbsp;</th>
                    </tr>
                  </thead>
                  <tbody>
                    {page.data.ranges.length === 0 ? (
                      <tr>
                        <td colSpan={9}>
                          <span className="label label-default">Nothing configured yet.</span> Until a
                          range exists, a relay scans nothing.
                        </td>
                      </tr>
                    ) : (
                      page.data.ranges.map((range) => (
                        <tr key={num(range, 'ID')}>
                          <td>{str(range, 'name') || 'Unnamed'}</td>
                          <td>{str(range, 'relay_name')}</td>
                          <td className="text-muted">{str(range, 'company_name')}</td>
                          {/* Un interval fara sediu nu ajuta la propunerea sediului unei
                              masini gasite in el; se vede care are si care nu. */}
                          <td className="text-muted">{str(range, 'office_name') || '–'}</td>
                          <td className="text-nowrap">
                            {str(range, 'ip_from')} – {str(range, 'ip_to')}
                          </td>
                          <td className="text-muted">{str(range, 'interval_label')}</td>
                          <td className="text-center">
                            {num(range, 'mode') === 2 ? (
                              <span className="label label-warning">Exclude</span>
                            ) : (
                              <span className="label label-success">Include</span>
                            )}
                          </td>
                          <td className="text-center">
                            {num(range, 'status') === 1 ? (
                              <span className="label label-success">yes</span>
                            ) : (
                              <span className="label label-default">no</span>
                            )}
                          </td>
                          <td className="text-right">
                            {page.data.canWrite ? (
                              <>
                                <button
                                  className="btn btn-default btn-xs scheduleEdit"
                                  data-relay={num(range, 'relay_device_id')}
                                  title="When this relay scans"
                                  type="button"
                                  onClick={() => setScheduleRelay(num(range, 'relay_device_id'))}
                                >
                                  <i className="fa fa-clock"></i>
                                </button>
                                <button
                                  className="btn btn-default btn-xs rangeEdit"
                                  data-id={num(range, 'ID')}
                                  type="button"
                                  onClick={() => setRangeId(num(range, 'ID'))}
                                >
                                  <i className="fa fa-pencil-alt"></i>
                                </button>
                                <button
                                  className="btn btn-danger btn-xs rangeRemove"
                                  data-id={num(range, 'ID')}
                                  type="button"
                                  onClick={async () => {
                                    if (!window.confirm('Remove this range? The relay stops scanning it.')) return
                                    const result = await removeRange(num(range, 'ID'))
                                    newAlert(result.message ?? '', result.status === 'OK' ? 'success' : 'danger')
                                    if (result.status === 'OK') await refresh()
                                  }}
                                >
                                  <i className="fa fa-times"></i>
                                </button>
                              </>
                            ) : null}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}
        {page.data.canRules ? (
          <div className="row">
            <div className="col-md-12">
              <SettingsHead
                strong="Ignore"
                rest="rules"
                subtitle="what discovery finds but should not record"
                tools={
                  page.data.canEditRules ? (
                    <button className="btn btn-success btn-sm" id="ruleAdd" type="button" onClick={() => setRuleId(0)}>
                      <i className="fas fa-plus-circle"></i> Add a rule
                    </button>
                  ) : undefined
                }
              />
              <p className="text-muted">A printer&apos;s spooler, a monitoring port, an address that belongs to something else.</p>
              <table className="table table-striped table-hover">
                <thead>
                  <tr>
                    <th>Ignores</th>
                    <th>What</th>
                    <th>Where</th>
                    <th>Why</th>
                    <th className="text-center">Active</th>
                    <th className="text-right">&nbsp;</th>
                  </tr>
                </thead>
                <tbody>
                  {page.data.rules.length === 0 ? (
                    <tr>
                      <td colSpan={6}>
                        <span className="label label-default">No rules.</span> Everything found is
                        recorded.
                      </td>
                    </tr>
                  ) : (
                    page.data.rules.map((rule) => (
                      <tr key={num(rule, 'ID')}>
                        <td>{page.data.ruleTypes[String(num(rule, 'rule_type'))] ?? ''}</td>
                        <td>
                          {num(rule, 'rule_type') === 1 ? (
                            <code>{str(rule, 'value_text')}</code>
                          ) : num(rule, 'rule_type') === 2 ? (
                            <code>{num(rule, 'value_num')}</code>
                          ) : (
                            <span className="text-nowrap">
                              {str(rule, 'ip_from')} – {str(rule, 'ip_to')}
                            </span>
                          )}
                        </td>
                        <td className="text-muted">{num(rule, 'scope') === 2 ? str(rule, 'relay_name') : 'Everywhere'}</td>
                        <td className="text-muted">{str(rule, 'reason')}</td>
                        <td className="text-center">
                          {num(rule, 'status') === 1 ? (
                            <span className="label label-success">yes</span>
                          ) : (
                            <span className="label label-default">no</span>
                          )}
                        </td>
                        <td className="text-right">
                          {page.data.canEditRules ? (
                            <>
                              <button className="btn btn-default btn-xs ruleEdit" data-id={num(rule, 'ID')} type="button" onClick={() => setRuleId(num(rule, 'ID'))}>
                                <i className="fa fa-pencil-alt"></i>
                              </button>
                              <button
                                className="btn btn-danger btn-xs ruleRemove"
                                data-id={num(rule, 'ID')}
                                type="button"
                                onClick={async () => {
                                  if (!window.confirm('Remove this ignore rule?')) return
                                  const result = await removeRule(num(rule, 'ID'))
                                  newAlert(result.message ?? '', result.status === 'OK' ? 'success' : 'danger')
                                  if (result.status === 'OK') await refresh()
                                }}
                              >
                                <i className="fa fa-times"></i>
                              </button>
                            </>
                          ) : null}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        ) : null}
      </div>
      {/*
       * Fereastra comuna a celor trei formulare, pe `Modal` - acelasi drum ca
       * la Create Company. Invelisul scris de mana punea doar `display: block`
       * pe `.modal`, deci fereastra se deschidea FARA valul din spate, fara
       * `body.modal-open` (pagina de dedesubt ramanea de derulat) si fara
       * Escape. Titlul si butoanele stau acum in fereastra, nu in fiecare
       * formular, iar butonul de salvare isi gaseste formularul dupa `form=`.
       */}
      <Modal
        id="scanRulesModal"
        open={rangeId !== null || ruleId !== null || scheduleRelay !== null}
        onClose={close}
        title={
          rangeId !== null
            ? rangeId === 0
              ? 'Add a range'
              : 'Edit range'
            : ruleId !== null
              ? ruleId === 0
                ? 'Add an ignore rule'
                : 'Edit ignore rule'
              : 'Scan schedule'
        }
        footer={
          <>
            <button type="button" className="btn btn-default" onClick={close}>
              Cancel
            </button>
            <button type="submit" form="scanRulesForm" className="btn btn-primary">
              Save
            </button>
          </>
        }
      >
        {rangeId !== null ? <RangeForm id={rangeId} onSaved={refresh} /> : null}
        {ruleId !== null ? <RuleForm id={ruleId} onSaved={refresh} /> : null}
        {scheduleRelay !== null ? <ScheduleForm relayId={scheduleRelay} onSaved={refresh} /> : null}
      </Modal>
    </>
  )
}

function notifyScan(message: string, type: string) {
  newAlert(message, type, type === 'success' ? 4000 : 7000, document.querySelector('.scanRulesNotifications'))
}

/* Inchiderea si salvarea stau acum in subsolul ferestrei, deci formularul nu
   mai primeste `onClose` - el scrie doar campurile. */
function RangeForm({ id, onSaved }: { id: number; onSaved(): Promise<void> }) {
  const page = useQuery(rangeFormQuery(id))
  /* Sertarele zonei (`SelectPicker`) sunt controlate, deci valoarea lor sta
     aici, nu in `defaultValue`. Se seamana cand raspunde serverul. */
  const [relay, setRelay] = useState('')
  const [office, setOffice] = useState('0')
  const [mode, setMode] = useState('1')
  const [status, setStatus] = useState('1')
  useEffect(() => {
    if (!page.data) return
    const row = page.data.range ?? {}
    setRelay(String(num(row, 'relay_device_id') || (page.data.relays[0] ? num(page.data.relays[0], 'ID') : '')))
    setOffice(String(num(row, 'office_id') || 0))
    setMode(String(num(row, 'mode') || 1))
    setStatus(page.data.range === null || num(row, 'status') === 1 ? '1' : '0')
  }, [page.data])
  if (!page.data) return <div>Loading…</div>
  const range = page.data.range ?? {}
  const isEdit = page.data.range !== null
  const onSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    try {
      const result = await saveRange(id, fields(event.currentTarget))
      if (result.status !== 'OK') {
        notifyScan(result.message ?? 'That could not be saved.', 'danger')
        return
      }
      await onSaved()
    } catch (err) {
      notifyScan(err instanceof Error ? err.message : 'That could not be saved.', 'danger')
    }
  }
  return (
    <form id="scanRulesForm" data-action="saveRange" onSubmit={onSubmit}>
      <input type="hidden" name="ID" value={isEdit ? num(range, 'ID') : 0} />
      <div className="form-group">
        <label htmlFor="rangeName">Name</label>
        <input type="text" className="form-control" id="rangeName" name="name" maxLength={100} defaultValue={str(range, 'name')} placeholder="e.g. HQ subnet" />
      </div>
      <div className="form-group">
        <label htmlFor="rangeRelay">Relay</label>
        <SelectPicker
          id="rangeRelay"
          className="form-control"
          name="relay_device_id"
          title="Select.."
          value={relay}
          onChange={setRelay}
          options={page.data.relays.map((row) => ({
            value: String(num(row, 'ID')),
            label: str(row, 'name') + (str(row, 'company_name') ? ' — ' + str(row, 'company_name') : ''),
          }))}
        />
        <span className="text-muted small">The customer follows the relay.</span>
      </div>
      {page.data.officesHere ? (
        <div className="form-group">
          <label htmlFor="rangeOffice">Office</label>
          <SelectPicker
            id="rangeOffice"
            className="form-control"
            name="office_id"
            title="Not set"
            value={office}
            onChange={setOffice}
            options={[
              { value: '0', label: 'Not set' },
              ...page.data.offices.map((row) => ({
                value: String(num(row, 'ID')),
                label: str(row, 'name') + (str(row, 'company_name') ? ' — ' + str(row, 'company_name') : ''),
              })),
            ]}
          />
          <span className="text-muted small">
            Machines found in this range are proposed to that office. It has to belong to the same customer as the
            relay.
          </span>
        </div>
      ) : null}
      <div className="row">
        <div className="col-md-6 form-group">
          <label htmlFor="rangeFrom">From address</label>
          <input type="text" className="form-control" id="rangeFrom" name="ip_from" defaultValue={str(range, 'ip_from_text') || str(range, 'ip_from')} placeholder="10.0.0.1" />
        </div>
        <div className="col-md-6 form-group">
          <label htmlFor="rangeTo">To address</label>
          <input type="text" className="form-control" id="rangeTo" name="ip_to" defaultValue={str(range, 'ip_to_text') || str(range, 'ip_to')} placeholder="10.0.0.254" />
        </div>
      </div>
      <div className="row">
        <div className="col-md-6 form-group">
          <label htmlFor="rangeMode">Mode</label>
          <SelectPicker
            id="rangeMode"
            className="form-control"
            name="mode"
            title="Select.."
            value={mode}
            onChange={setMode}
            options={Object.entries(page.data.modes).map(([value, label]) => ({ value, label }))}
          />
        </div>
        <div className="col-md-6 form-group">
          <label htmlFor="rangeStatus">Active</label>
          <SelectPicker
            id="rangeStatus"
            className="form-control"
            name="status"
            title="Select.."
            value={status}
            onChange={setStatus}
            options={[
              { value: '1', label: 'Yes' },
              { value: '0', label: 'No' },
            ]}
          />
        </div>
      </div>
    </form>
  )
}

function ScheduleForm({ relayId, onSaved }: { relayId: number; onSaved(): Promise<void> }) {
  const page = useQuery(scheduleFormQuery(relayId))
  const [interval, setInterval] = useState(1)
  const [run, setRun] = useState('0')
  const [moment, setMoment] = useState('1')
  const [followUp, setFollowUp] = useState('0')
  useEffect(() => {
    if (!page.data?.current) return
    setInterval(num(page.data.current, 'recurrence_interval') || 1)
    setRun(num(page.data.current, 'recurrence') === 1 ? '1' : '0')
    setMoment(String(num(page.data.current, 'recurrence_moment') || 1))
    setFollowUp(String(num(page.data.current, 'follow_up') || 0))
  }, [page.data])
  if (!page.data) return <div>Loading…</div>
  const current = page.data.current ?? {}
  const time = str(current, 'recurrence_time').slice(0, 5) || '03:00'
  const relayName = page.data.relays.find((relay) => num(relay, 'ID') === relayId)
  const days = ['', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday']
  const onSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    try {
      const result = await saveSchedule(fields(event.currentTarget))
      if (result.status !== 'OK') {
        notifyScan(result.message ?? 'That could not be saved.', 'danger')
        return
      }
      await onSaved()
    } catch (err) {
      notifyScan(err instanceof Error ? err.message : 'That could not be saved.', 'danger')
    }
  }
  return (
    <form id="scanRulesForm" data-action="saveSchedule" onSubmit={onSubmit}>
      <input type="hidden" name="relay_device_id" value={relayId} />
      <p className="text-muted">
        When <strong>{relayName ? str(relayName, 'name') : 'this relay'}</strong> scans everything in
        its ranges.
      </p>
      <div className="form-group">
        <label htmlFor="schedEnabled">Run automatically</label>
        <SelectPicker
          id="schedEnabled"
          className="form-control"
          name="recurrence"
          title="Select.."
          value={run}
          onChange={setRun}
          options={[
            { value: '1', label: 'Yes' },
            { value: '0', label: 'No — only when started manually' },
          ]}
        />
      </div>
      <div className="row">
        <div className="col-md-4 form-group">
          <label htmlFor="schedInterval">How often</label>
          <SelectPicker
            id="schedInterval"
            className="form-control"
            name="recurrence_interval"
            title="Select.."
            value={String(interval)}
            onChange={(value) => setInterval(Number(value))}
            options={Object.entries(page.data.intervals).map(([value, label]) => ({ value, label }))}
          />
        </div>
        <div className="col-md-4 form-group schedWeekly" style={{ display: interval === 2 ? undefined : 'none' }}>
          <label htmlFor="schedDay">Day of the week</label>
          <SelectPicker
            id="schedDay"
            className="form-control"
            name={interval === 2 ? 'recurrence_moment' : undefined}
            title="Select.."
            value={moment}
            onChange={setMoment}
            options={[1, 2, 3, 4, 5, 6, 7].map((i) => ({ value: String(i), label: days[i] ?? '' }))}
          />
        </div>
        <div className="col-md-4 form-group schedMonthly" style={{ display: interval === 3 ? undefined : 'none' }}>
          <label htmlFor="schedDayOfMonth">Day of the month</label>
          <SelectPicker
            id="schedDayOfMonth"
            className="form-control"
            name={interval === 3 ? 'recurrence_moment' : undefined}
            title="Select.."
            value={moment}
            onChange={setMoment}
            options={Array.from({ length: 28 }, (_, i) => ({ value: String(i + 1), label: String(i + 1) }))}
          />
        </div>
        <div className="col-md-4 form-group">
          <label htmlFor="schedTime">At</label>
          <input type="time" className="form-control" id="schedTime" name="recurrence_time" defaultValue={time} />
        </div>
      </div>
      <div className="form-group">
        <label htmlFor="schedFollowUp">What the scheduled scan does</label>
        <SelectPicker
          id="schedFollowUp"
          className="form-control"
          name="follow_up"
          title="Select.."
          value={followUp}
          onChange={setFollowUp}
          options={[
            { value: '0', label: 'Find the devices only (address, MAC, name, common ports)' },
            { value: '3', label: 'Find the devices, then scan their services (ports, banners, OS)' },
            { value: '4', label: 'Find the devices, then run the extended scan (services, SNMP, NetBIOS, mDNS)' },
          ]}
        />
        <p className="help-block">
          Finding the devices also tries a short list of common ports, enough to tell a printer from a server. The
          services scan adds banners and the operating system; without it the devices show &ldquo;No services scan
          yet&rdquo;. It knocks on every address in the ranges, so pick a quiet hour. The hour is the server&apos;s: the
          scan is started from here, not by the relay&apos;s own clock.
        </p>
      </div>
    </form>
  )
}

function RuleForm({ id, onSaved }: { id: number; onSaved(): Promise<void> }) {
  const page = useQuery(ruleFormQuery(id))
  const [type, setType] = useState(1)
  const [scope, setScope] = useState(1)
  const [relay, setRelay] = useState('')
  const [status, setStatus] = useState('1')
  useEffect(() => {
    if (!page.data) return
    const row = page.data.rule
    setRelay(String(num(row ?? {}, 'relay_device_id') || (page.data.relays[0] ? num(page.data.relays[0], 'ID') : '')))
    setStatus(row === null || num(row, 'status') === 1 ? '1' : '0')
    if (!row) return
    setType(num(row, 'rule_type') || 1)
    setScope(num(row, 'scope') || 1)
  }, [page.data])
  if (!page.data) return <div>Loading…</div>
  const rule = page.data.rule ?? {}
  const isEdit = page.data.rule !== null
  const onSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    try {
      const result = await saveRule(id, fields(event.currentTarget))
      if (result.status !== 'OK') {
        notifyScan(result.message ?? 'That could not be saved.', 'danger')
        return
      }
      await onSaved()
    } catch (err) {
      notifyScan(err instanceof Error ? err.message : 'That could not be saved.', 'danger')
    }
  }
  return (
    <form id="scanRulesForm" data-action="saveRule" onSubmit={onSubmit}>
      <input type="hidden" name="ID" value={isEdit ? num(rule, 'ID') : 0} />
      <div className="row">
        <div className="col-md-6 form-group">
          <label htmlFor="ruleType">Ignore</label>
          <SelectPicker
            id="ruleType"
            className="form-control"
            name="rule_type"
            title="Select.."
            value={String(type)}
            onChange={(value) => setType(Number(value))}
            options={Object.entries(page.data.types).map(([value, label]) => ({ value, label }))}
          />
        </div>
        <div className="col-md-6 form-group">
          <label htmlFor="ruleScope">Where it applies</label>
          {/*
           * "One relay" era desenat si cand nu exista niciun releu, doar
           * `disabled`, cu motivul lipit de eticheta lui. Sertarul zonei n-are
           * randuri stinse, si nici n-are de ce: un rand pe care nu-l poate
           * alege nimeni nu spune mai mult decat un rand care lipseste, iar
           * motivul se scrie o data, dedesubt, unde se citeste.
           */}
          <SelectPicker
            id="ruleScope"
            className="form-control"
            name="scope"
            title="Select.."
            value={String(scope)}
            onChange={(value) => setScope(Number(value))}
            options={Object.entries(page.data.scopes)
              .filter(([value]) => Number(value) !== 2 || page.data.relays.length > 0)
              .map(([value, label]) => ({ value, label }))}
          />
          {page.data.relays.length === 0 ? (
            <span className="text-muted small">Only &quot;Everywhere&quot; — no machine is marked as a relay yet.</span>
          ) : null}
        </div>
      </div>
      <div className="form-group ruleRelay" style={{ display: scope === 2 ? undefined : 'none' }}>
        <label htmlFor="ruleRelayId">Relay</label>
        <SelectPicker
          id="ruleRelayId"
          className="form-control"
          name="relay_device_id"
          title="Select.."
          value={relay}
          onChange={setRelay}
          options={page.data.relays.map((row) => ({ value: String(num(row, 'ID')), label: str(row, 'name') }))}
        />
      </div>
      <div className="form-group ruleProcess" style={{ display: type === 1 ? undefined : 'none' }}>
        <label htmlFor="ruleText">Process name</label>
        <input type="text" className="form-control" id="ruleText" name="value_text" maxLength={190} defaultValue={str(rule, 'value_text')} placeholder="spoolsv.exe" />
      </div>
      <div className="form-group rulePort" style={{ display: type === 2 ? undefined : 'none' }}>
        <label htmlFor="ruleNum">Port</label>
        <input type="number" className="form-control" id="ruleNum" name="value_num" min={1} max={65535} defaultValue={num(rule, 'value_num') || ''} placeholder="9100" />
      </div>
      <div className="row ruleAddress" style={{ display: type === 3 ? undefined : 'none' }}>
        <div className="col-md-6 form-group">
          <label htmlFor="ruleFrom">From address</label>
          <input type="text" className="form-control" id="ruleFrom" name="ip_from" defaultValue={str(rule, 'ip_from_text') || str(rule, 'ip_from')} />
        </div>
        <div className="col-md-6 form-group">
          <label htmlFor="ruleTo">To address</label>
          <input type="text" className="form-control" id="ruleTo" name="ip_to" defaultValue={str(rule, 'ip_to_text') || str(rule, 'ip_to')} />
        </div>
      </div>
      <div className="row">
        <div className="col-md-8 form-group">
          <label htmlFor="ruleReason">Why</label>
          <input type="text" className="form-control" id="ruleReason" name="reason" maxLength={255} defaultValue={str(rule, 'reason')} placeholder="Printer spooler, not a service worth mapping" />
        </div>
        <div className="col-md-4 form-group">
          <label htmlFor="ruleStatus">Active</label>
          <SelectPicker
            id="ruleStatus"
            className="form-control"
            name="status"
            title="Select.."
            value={status}
            onChange={setStatus}
            options={[
              { value: '1', label: 'Yes' },
              { value: '0', label: 'No' },
            ]}
          />
        </div>
      </div>
    </form>
  )
}

